import { useId, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import { formatMoney } from "@/lib/format";
import type { AgingReport } from "@/api/types";
import "./ReportsAging.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/** Today, UTC — same server-clock default `AgingReportEndpoints`' own `asOf` defaulting
 *  uses, restated here only for the form's own initial value. */
function defaultAsOf(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * AR/AP aging detail — Reports' worksheet of every outstanding bill and invoice, bucketed
 * current/1-30/31-60/61-90/90+ by days overdue as of a date the owner chooses. ef-052-2,
 * ADR 0009 §6 ("Reports is one picker" — a report renders inside the template at
 * `/reports/{slug}`, no area tab strip of its own; "All reports" below is the only way
 * back).
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Apply IS PRESSED — same "no guessed window" discipline
 * `ReportsProductionSales.tsx`'s own remarks give, restated here for the identical
 * reason: an aging figure read against a date nobody asked for is a number a farmer did
 * not request. The As of input still carries today's date as a sensible default so the
 * form is never an empty field.
 * </p>
 *
 * <p>
 * ONE DATE PICKER, NOT A RANGE — deliberately does not copy `ReportsProductionSales.tsx`'s
 * own From/To layout: an aging report is a snapshot as of one date, not a window.
 * </p>
 *
 * <p>
 * NO ENTITY PICKER — same reasoning `ReportsProductionSales.tsx`'s own remarks give:
 * `AgingReportEndpoints` resolves the tenant's own single/oldest entity when `entityId` is
 * omitted, which is every farm this booth demo models.
 * </p>
 */
export function ReportsAging() {
  const asOfId = useId();

  const [asOf, setAsOf] = useState(defaultAsOf);

  const [report, setReport] = useState<AgingReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const canSubmit = asOf !== "" && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getAgingReport({ asOf });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the AR/AP aging detail report."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the report,
   * same reasoning `ReportsProductionSales.tsx`'s identical download gives: paying for
   * the whole CSV body on every date change would be wasted work for the visits that
   * never press the button.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!canSubmit) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getAgingCsv({ asOf });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsProductionSales.tsx`'s identical
      // download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-aging-${asOf}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the aging detail file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device `ReportsProductionSales.tsx`'s own handler already
    // uses rather than threading a `navigate` prop through every screen this job
    // touches: pushState doesn't fire popstate on its own, so App.tsx's existing
    // listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const rows = report?.rows ?? [];
  const isEmpty = status === "ready" && rows.length === 0;

  return (
    <main className="aging">
      <PageHeader
        title="AR/AP aging detail"
        subtitle="Every outstanding bill and invoice, bucketed by how overdue it is"
      />

      <a href="/reports" className="aging__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="aging__panel"
        aria-labelledby="aging-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="aging-heading" className="aging__heading">
          Worksheet
        </h2>

        <form className="aging__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="aging__field">
            <label htmlFor={asOfId}>As of</label>
            <input
              id={asOfId}
              type="date"
              value={asOf}
              onChange={(event) => setAsOf(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary aging__apply" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Apply"}
          </button>
        </form>

        <Why>
          A document is <strong>current</strong> when its due date has not yet passed as of{" "}
          <strong>As of</strong>; otherwise it's grouped by 30-day steps of how overdue it
          is. Voided documents never appear.
        </Why>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the AR/AP aging detail report."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing outstanding as of this date."
            detail="No bill or invoice has a balance left to age."
          />
        )}

        {status === "ready" && rows.length > 0 && (
          <>
            <div
              className="aging__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="aging-heading"
            >
              <table className="aging__table ruled-rows">
                <caption className="aging__caption">
                  {rows.length} {rows.length === 1 ? "line" : "lines"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Kind</th>
                    <th scope="col">Counterparty</th>
                    <th scope="col" className="aging__num">Outstanding</th>
                    <th scope="col">Bucket</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.documentId}>
                      <th scope="row" className="aging__kind">{row.kind}</th>
                      <td>{row.counterparty}</td>
                      <td className="aging__num aging__amount">{formatMoney(row.outstanding)}</td>
                      <td>
                        <span className="aging__bucket">{row.bucket}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="aging__export">
              <h3 className="aging__export-title">AR/AP aging detail for your records</h3>
              <p className="aging__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary aging__export-btn"
                disabled={csvState.kind === "busy" || !canSubmit}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="aging__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="aging__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
